/**
 * Pre-localized English onboarding flow definition in Galapagos design system
 * catalog format.
 *
 * ADR-017: GenUI specs are localized before the client renders them. This file
 * is the bundled `en` snapshot used until the flow source resolves remotely.
 *
 * Platform-free by construction: the mobile app composes these screens with
 * its two mobile-only screens (`sona_intro`, `rewards_intro`, authored in the
 * app) into the bundled mobile definition, and the web flow host consumes the
 * sibling `web-onboarding-flow` composition, which omits them.
 *
 * Each screen spec uses only catalog components: Screen, Stack, Text, TextInput,
 * SingleSelect, NumberPicker, NumberTile, MetricCard, Button, TextButton,
 * CharacterIllustration, AnimatedIllustration, Pagination.
 *
 * Every screen here is heading text plus bare input controls rather than a card
 * stack, so each one sets the wider screen-edge margin explicitly instead of
 * taking the card-oriented default (ADR-091).
 *
 * Navigation rules encode the program-specific usage and screening questions.
 * The final question routes directly to the mobile rewards introduction and
 * conversation-mode stage. Web completes at the final question after omitting
 * mobile-only introductions. The weekly usage summary remains a question.
 */

import { colors } from '@galapagos/design-system/colors';
import {
  INLINE_STEPPER_ROW_WIDTH,
  STEPPER_GAP,
  REFERENCE_COLUMN_WIDTH,
} from '@galapagos/design-system/sizing';
import {
  BASELINE_WEEK_SUMMARY_POINTER,
  TYPICAL_WEEK_SUMMARY_POINTER,
  WEEK_ANCHOR_DATE_POINTER,
  SUMMARY_BULLETS_FOCUS_POINTER,
  SUMMARY_BULLETS_MOTIVATION_POINTER,
} from '@galapagos/shared';
import type { FlowDefinition, FlowScreen, NavigationRule } from '@galapagos/shared';
import { calculateWeekSummary } from '../calculate-week-summary';

// ---------------------------------------------------------------------------
// Helper: the bundled flow's screen record. Step keys are opaque strings (flow
// data, not a client contract) and each screen reuses the shared GenUI
// `FlowScreen` shape — the client defines no flow-specific step-key type.
// ---------------------------------------------------------------------------

type OnboardingScreens = Record<string, FlowScreen>;

/** screen_ids of the usage screens that name calendar days. Exported so the
 * render-time day-date transform can target them without re-deriving the
 * strings (all shared across substances). */
export const WEEKLY_USAGE_SCREEN_ID = 'onboarding-weekly-usage';
export const TYPICAL_USAGE_SCREEN_ID = 'onboarding-typical-usage';
export const WEEK_SUMMARY_SCREEN_ID = 'onboarding-week-summary';

type CatalogElement = {
  type: string;
  props?: Record<string, unknown>;
  children?: string[];
  on?: unknown;
  /**
   * Runtime-reactive show/hide, evaluated against live screen state on every
   * render (unlike a build-time JS branch baked into the static spec). See
   * `@json-render/core`'s `VisibilityCondition` — `{ $state, eq/neq/gt/gte/
   * lt/lte/includes, not }`, composable via `$and`/`$or`.
   */
  visible?: unknown;
  /**
   * Fires a tool chain whenever the value at a state path changes — same
   * `{ tool_name, parameters }` binding shape as `on`, keyed by JSON Pointer
   * path instead of an event name.
   */
  watch?: unknown;
};

type UsageStepBase = 'weekly_drinks' | 'week_summary' | 'typical_drinks';

// Substance-specific "what counts as one <unit>?" guidance shown in an
// expandable callout on the weekly picker. Either a set of visual equivalents
// (alcohol: standard drink = liquor = wine = beer) or a text explanation
// (cannabis and future nicotine/vaping/smokeless variants). Optional — variants
// without guidance render no callout.
type UsageUnitGuide =
  | { title: string; kind: 'text'; body: string }
  | {
      title: string;
      kind: 'equivalents';
      items: { icon: string; label: string; sublabel?: string }[];
    };

type UsageVariant = {
  /** null = the neutral fallback variant (poly selections, unknown values). */
  programKey: string | null;
  legacyProgramValues?: string[];
  suffix: string;
  unitGuide?: UsageUnitGuide;
  weeklyAccessibilityTitle: string;
  weeklySubtitle: string;
  typicalAccessibilityTitle: string;
  typicalSubtitle: string;
  totalLabel: string;
  freeLabel: string;
  freeLegendLabel: string;
  activeLegendLabel: string;
  dayAccessibilitySuffix: string;
  dayAccessibilitySuffixOne: string;
  /**
   * Further flow-state answers a member must hold to take this variant, beyond
   * the programme itself (the nicotine type picked on the substance step). A
   * variant with none is the programme's own; one with some is a refinement
   * routed ahead of it in the programme's priority slot.
   */
  branchState?: Record<string, string>;
};

// Neutral usage fallback — substance-free copy for poly selections or any
// future select value not yet given its own variant. Mirrors the neutral goal
// step: the usage catch-all lands here, never on the alcohol screens.
const NEUTRAL_USAGE_VARIANT: UsageVariant = {
  programKey: null,
  suffix: '_neutral',
  weeklyAccessibilityTitle: 'How many times last week?',
  weeklySubtitle: 'How many times each day? Give your best guess.',
  typicalAccessibilityTitle: 'How many times in a typical week?',
  typicalSubtitle: 'How many times each day in a typical week?',
  totalLabel: 'Total times',
  freeLabel: 'Use-free',
  freeLegendLabel: 'Use-free',
  activeLegendLabel: 'Use days',

  dayAccessibilitySuffix: 'times',
  dayAccessibilitySuffixOne: 'time',
};

const USAGE_VARIANTS: UsageVariant[] = [
  {
    programKey: 'alcohol',
    legacyProgramValues: ['focus1'],
    suffix: '',
    weeklyAccessibilityTitle: 'How many drinks did you have last week?',
    weeklySubtitle: 'How many drinks did you have each day? Give your best guess.',
    typicalAccessibilityTitle: 'How many drinks do you have in a typical week?',
    typicalSubtitle: 'How many drinks do you have each day in a typical week?',
    totalLabel: 'Total drinks',
    freeLabel: 'Alcohol-free',
    freeLegendLabel: 'Drink-free',
    activeLegendLabel: 'Drinking days',

    dayAccessibilitySuffix: 'drinks',
    dayAccessibilitySuffixOne: 'drink',
    unitGuide: {
      title: 'What counts as one drink?',
      kind: 'equivalents',
      items: [
        { icon: 'alcohol', label: 'Standard\ndrink' },
        { icon: 'liquor', label: 'Hard liquor', sublabel: '1.5 fl.oz' },
        { icon: 'wine', label: 'Wine', sublabel: '5 fl.oz' },
        { icon: 'beer', label: 'Beer', sublabel: '12 fl.oz' },
      ],
    },
  },
  {
    programKey: 'tobacco',
    legacyProgramValues: ['focus2'],
    suffix: '_tobacco',
    weeklyAccessibilityTitle: 'How many times did you use tobacco last week?',
    weeklySubtitle: 'How many times did you use tobacco each day? Give your best guess.',
    typicalAccessibilityTitle: 'How many times do you use tobacco in a typical week?',
    typicalSubtitle: 'How many times do you use tobacco each day in a typical week?',
    totalLabel: 'Total times',
    freeLabel: 'Tobacco-free',
    freeLegendLabel: 'Tobacco-free',
    activeLegendLabel: 'Tobacco use days',

    dayAccessibilitySuffix: 'times using tobacco',
    dayAccessibilitySuffixOne: 'time using tobacco',
    // Placeholder copy — final vaping/smokeless wording pending. Covers all
    // nicotine (cigarettes, vaping, smokeless) since they share this variant.
    unitGuide: {
      title: 'What counts as one use?',
      kind: 'text',
      body: 'Any tobacco or nicotine use in 1 hour = 1 use. For example — if you vaped 3 times from 10 to 11, count it as 1 use. If you used at 10 and again at 11, count it as 2 uses.',
    },
  },
  {
    programKey: 'opioid',
    legacyProgramValues: ['focus3', 'pain'],
    suffix: '_opioid',
    weeklyAccessibilityTitle: 'How many times did you take your pain medication last week?',
    weeklySubtitle:
      'How many times did you take your pain medication each day? Give your best guess.',
    typicalAccessibilityTitle: 'How many times do you take your pain medication in a typical week?',
    typicalSubtitle: 'How many times do you take your pain medication each day in a typical week?',
    totalLabel: 'Total times',
    freeLabel: 'Medication-free',
    freeLegendLabel: 'Medication-free',
    activeLegendLabel: 'Medication use days',

    dayAccessibilitySuffix: 'times taking medication',
    dayAccessibilitySuffixOne: 'time taking medication',
  },
  {
    programKey: 'stimulants',
    legacyProgramValues: ['focus4', 'stimulant'],
    suffix: '_stimulants',
    weeklyAccessibilityTitle: 'How many times did you use stimulants last week?',
    weeklySubtitle: 'How many times did you use stimulants each day? Give your best guess.',
    typicalAccessibilityTitle: 'How many times do you use stimulants in a typical week?',
    typicalSubtitle: 'How many times do you use stimulants each day in a typical week?',
    totalLabel: 'Total times',
    freeLabel: 'Use-free',
    freeLegendLabel: 'Use-free',
    activeLegendLabel: 'Use days',

    dayAccessibilitySuffix: 'times using it',
    dayAccessibilitySuffixOne: 'time using it',
  },
  {
    programKey: 'cannabis',
    legacyProgramValues: ['focus5'],
    suffix: '_cannabis',
    weeklyAccessibilityTitle: 'How many times did you use cannabis last week?',
    weeklySubtitle: 'How many times did you use cannabis each day? Give your best guess.',
    typicalAccessibilityTitle: 'How many times do you use cannabis in a typical week?',
    typicalSubtitle: 'How many times do you use cannabis each day in a typical week?',
    totalLabel: 'Total times',
    freeLabel: 'Cannabis-free',
    freeLegendLabel: 'Cannabis-free',
    activeLegendLabel: 'Cannabis use days',

    dayAccessibilitySuffix: 'times using cannabis',
    dayAccessibilitySuffixOne: 'time using cannabis',
    unitGuide: {
      title: 'What counts as one block?',
      kind: 'text',
      body: 'Any cannabis use in 1 hour = 1 block. For example — if you used cannabis 3 times from 10 to 11, count it as 1 block. If you used cannabis at 10 and then at 11, count it as 2 blocks.',
    },
  },
  NEUTRAL_USAGE_VARIANT,
];

// Nicotine-type refinements of the tobacco baseline. A smoker counts
// cigarettes and a vaper counts vaping sessions, the same units their check-in
// asks for; smokeless and an unanswered type keep the tobacco variant's
// hour-block uses. The answers land on the same day paths as every variant, so
// the stored baseline unit stays `use`.
const USAGE_SUBTYPE_VARIANTS: UsageVariant[] = [
  {
    programKey: 'tobacco',
    suffix: '_tobacco_cigarettes',
    branchState: { '/screen/tobacco_subtype': 'cigarettes' },
    weeklyAccessibilityTitle: 'How many cigarettes did you smoke last week?',
    weeklySubtitle: 'How many cigarettes did you smoke each day? Give your best guess.',
    typicalAccessibilityTitle: 'How many cigarettes do you smoke in a typical week?',
    typicalSubtitle: 'How many cigarettes do you smoke each day in a typical week?',
    totalLabel: 'Total cigarettes',
    freeLabel: 'Smoke-free',
    freeLegendLabel: 'Smoke-free',
    activeLegendLabel: 'Smoking days',

    dayAccessibilitySuffix: 'cigarettes',
    dayAccessibilitySuffixOne: 'cigarette',
    unitGuide: {
      title: 'What counts as one cigarette?',
      kind: 'text',
      body: "Count every cigarette you light, even if you don't finish it. Roll-ups count the same way.",
    },
  },
  {
    programKey: 'tobacco',
    suffix: '_tobacco_vaping',
    branchState: { '/screen/tobacco_subtype': 'vaping' },
    weeklyAccessibilityTitle: 'How many vaping sessions did you have last week?',
    weeklySubtitle: 'How many vaping sessions did you have each day? Give your best guess.',
    typicalAccessibilityTitle: 'How many vaping sessions do you have in a typical week?',
    typicalSubtitle: 'How many vaping sessions do you have each day in a typical week?',
    totalLabel: 'Total sessions',
    freeLabel: 'Vape-free',
    freeLegendLabel: 'Vape-free',
    activeLegendLabel: 'Vaping days',

    dayAccessibilitySuffix: 'vaping sessions',
    dayAccessibilitySuffixOne: 'vaping session',
    unitGuide: {
      title: 'What counts as one session?',
      kind: 'text',
      body: 'One session is each time you pick up your vape and use it, however many puffs you take. If you put it down and vape again later, that is a new session.',
    },
  },
];

/** Every variant that gets its own baseline screens: one per programme plus the refinements. */
const ALL_USAGE_VARIANTS: UsageVariant[] = [...USAGE_VARIANTS, ...USAGE_SUBTYPE_VARIANTS];

// Programs whose usage branch routes through TAPS 2 screening steps before
// summary, each with its question steps in ask order. Flow-authored DATA
// mirroring the catalog's per-program `taps2` declarations — a drift test
// cross-checks the two, so a catalog set without its flow steps (or vice
// versa) fails in CI rather than shipping silently. Stimulants declares no
// set: its only candidate question is still an open clinical decision, so its
// baseline exits straight to summary like the behavioral programs.
export const TAPS2_STEP_CHAINS: Readonly<Record<string, readonly string[]>> = {
  tobacco: ['taps2_smoke_timing'],
  alcohol: ['taps2_alcohol_concern'],
  opioid: ['taps2_opioid_misuse', 'taps2_opioid_concern'],
  cannabis: ['taps2_cannabis_concern'],
};
const TAPS2_PROGRAM_KEYS = Object.keys(TAPS2_STEP_CHAINS);

// The single-question screens share one skeleton; only the copy and the bound
// answer key vary. All are yes/no items — the catalog's `positive_values`
// carries which answer counts as a positive screen, never the flow.
type Taps2YesNoQuestion = {
  stepKey: string;
  heading: string;
  hint: string;
  /** Hidden accessible label for the SingleSelect. */
  label: string;
};

const TAPS2_YES_NO_QUESTIONS: readonly Taps2YesNoQuestion[] = [
  {
    stepKey: 'taps2_alcohol_concern',
    heading: 'In the last 3 months, has anyone expressed concern about your drinking?',
    hint: 'This helps us get a clearer picture of your pattern.',
    label: 'Concern about drinking',
  },
  {
    stepKey: 'taps2_opioid_misuse',
    heading: 'In the last 3 months, have you used a medication other than as prescribed?',
    hint: 'For example, taking more of it, or taking it for longer, than prescribed.',
    label: 'Medication used other than as prescribed',
  },
  {
    stepKey: 'taps2_opioid_concern',
    heading:
      'In the last 3 months, has anyone expressed concern about your prescription medication use?',
    hint: 'This helps us get a clearer picture of your pattern.',
    label: 'Concern about prescription medication use',
  },
  {
    stepKey: 'taps2_cannabis_concern',
    heading: 'In the last 3 months, has anyone expressed concern about your cannabis use?',
    hint: 'This helps us get a clearer picture of your pattern.',
    label: 'Concern about cannabis use',
  },
];

const PRIMARY_PROGRAM_PRIORITY = ['opioid', 'stimulants', 'alcohol', 'cannabis', 'tobacco'];
const USAGE_VARIANTS_BY_PRIMARY_PRIORITY = PRIMARY_PROGRAM_PRIORITY.map((programKey) => {
  const variant = USAGE_VARIANTS.find((candidate) => candidate.programKey === programKey);
  if (!variant) {
    throw new Error(`Missing usage variant for primary program priority ${programKey}`);
  }
  return variant;
});

const USAGE_DAY_PICKERS = [
  { key: 'mon', label: 'Monday', shortLabel: 'Mon', field: 'day_1', typicalField: 'typical_day_1' },
  {
    key: 'tue',
    label: 'Tuesday',
    shortLabel: 'Tue',
    field: 'day_2',
    typicalField: 'typical_day_2',
  },
  {
    key: 'wed',
    label: 'Wednesday',
    shortLabel: 'Wed',
    field: 'day_3',
    typicalField: 'typical_day_3',
  },
  {
    key: 'thu',
    label: 'Thursday',
    shortLabel: 'Thu',
    field: 'day_4',
    typicalField: 'typical_day_4',
  },
  { key: 'fri', label: 'Friday', shortLabel: 'Fri', field: 'day_5', typicalField: 'typical_day_5' },
  {
    key: 'sat',
    label: 'Saturday',
    shortLabel: 'Sat',
    field: 'day_6',
    typicalField: 'typical_day_6',
  },
  { key: 'sun', label: 'Sunday', shortLabel: 'Sun', field: 'day_7', typicalField: 'typical_day_7' },
] as const;

const WEEK_SUMMARY_VALUES = [0, 0, 0, 3, 0, 3, 2] as const;
const WEEK_SUMMARY_FREE_COLOR = colors.mossGreen['500'];
const WEEK_SUMMARY_ACTIVE_COLOR = colors.cerulean['400'];
// The four derived write targets below, and the anchor path, are the SHARED
// registry's constants rather than literals authored here. `packages/shared`
// declares them so a server-side reader of the persisted answers can tell a
// tool's generated output from a member's answer; authoring them here as
// strings would leave two lists to drift, and the drift guard in this package
// asserts the flow declares exactly what the registry names.
const BASELINE_WEEK_SUMMARY_PATH = BASELINE_WEEK_SUMMARY_POINTER;
const TYPICAL_WEEK_SUMMARY_PATH = TYPICAL_WEEK_SUMMARY_POINTER;

// Column-header layout (typical-week comparison screen): mirrors NumberPicker's
// own row geometry — a flex:1 spacer matching its label column, a slot the
// width of its reference tile (so "Last 7 days" centers directly over the
// tile numbers below, even though the label itself is wider and overflows the
// slot), a gap matching the tile's own trailing margin, then a slot the width
// of the stepper row for "Typical week". This guarantees vertical alignment
// with the row below regardless of either label's own hug-text width.
const LAST_WEEK_HEADER_BACKGROUND = colors.background.primary;
const TYPICAL_WEEK_HEADER_BACKGROUND = colors.button.primary;
const TYPICAL_WEEK_HEADER_TEXT_COLOR = colors.text.inverted;

function usageStepKey(base: UsageStepBase, variant: UsageVariant): string {
  return `${base}${variant.suffix}`;
}

function usageBranchConditions(variant: UsageVariant): NavigationRule['when'][] {
  // The neutral fallback has no branch conditions — it is reached only via the
  // catch-all, never matched on a program key.
  if (variant.programKey === null) return [];
  return [
    { '/screen/program_key': variant.programKey },
    { '/screen/substance': { $in: [variant.programKey, ...(variant.legacyProgramValues ?? [])] } },
  ];
}

function usageValuePaths(kind: 'weekly' | 'typical'): string[] {
  return USAGE_DAY_PICKERS.map(
    (day) => `/screen/${kind === 'typical' ? day.typicalField : day.field}`,
  );
}

function buildCalculateWeekSummaryTool(
  kind: 'weekly' | 'typical',
  variant: UsageVariant,
  targetPath: string,
) {
  return {
    tool_name: 'calculate_week_summary',
    parameters: {
      sourcePaths: usageValuePaths(kind),
      targetPath,
      activeColor: WEEK_SUMMARY_ACTIVE_COLOR,
      freeColor: WEEK_SUMMARY_FREE_COLOR,
      totalLabel: variant.totalLabel,
      freeLabel: variant.freeLabel,
      dayLabels: USAGE_DAY_PICKERS.map((day) => day.label),
      dayAccessibilitySuffix: variant.dayAccessibilitySuffix,
      dayAccessibilitySuffixOne: variant.dayAccessibilitySuffixOne,
      // Declared write target for the device-local date anchoring the rolling
      // day window; both summaries stamp the same key (same sitting). The
      // anchor VALUE is injected at resolve by applyWeeklyDayDates so it comes
      // from the same clock as the day labels the member sees.
      anchorPath: WEEK_ANCHOR_DATE_POINTER,
    },
  };
}

// Personalised summary bullets that echo the member's own selections. Each
// bullet joins short display phrases (mapped from the member's answer values)
// into a sentence template. The value→phrase maps are authored flow DATA
// (ADR-015) — the host tool and the generic engine stay condition-agnostic
// (ADR-046 Rule 6/7). Derived into display-only state paths the summary screen's
// `$bindState` bindings read; the member's own answers are never mutated.
const SUMMARY_FOCUS_PATH = SUMMARY_BULLETS_FOCUS_POINTER;
const SUMMARY_MOTIVATION_PATH = SUMMARY_BULLETS_MOTIVATION_POINTER;

// Primary focus (substance screen) values → short focus terms.
// Includes legacy `/screen/substance` scalar values (focus1–focus5, plus
// aliases like 'pain' and 'stimulant') so resumed members carrying old
// vocabulary still get personalised summary bullets.
const FOCUS_PRIMARY_LABELS: Record<string, string> = {
  alcohol: 'alcohol',
  tobacco: 'tobacco',
  opioid: 'opioids',
  stimulants: 'stimulants',
  cannabis: 'cannabis',
  // Legacy aliases (from USAGE_VARIANTS.legacyProgramValues).
  focus1: 'alcohol',
  focus2: 'tobacco',
  focus3: 'opioids',
  pain: 'opioids',
  focus4: 'stimulants',
  stimulant: 'stimulants',
  focus5: 'cannabis',
};

// Secondary focus (condition screen) values → short focus terms.
const FOCUS_SECONDARY_LABELS: Record<string, string> = {
  gambling: 'gambling',
  gaming: 'gaming',
  eating: 'eating',
};

// Motivation values → short phrases slotted after "Your motivation includes …".
const MOTIVATION_PHRASES: Record<string, string> = {
  health: 'looking after your health',
  relationship: 'improving your relationship',
  control: 'feeling more in control',
  provider: 'a recommendation from your provider',
  curious: 'understanding your habits',
};

function buildDeriveSummaryBulletsTool() {
  return {
    tool_name: 'derive_summary_bullets',
    parameters: {
      bullets: [
        {
          // "You want to focus on gambling and alcohol." — secondary focus then
          // primary, echoing whatever the member selected. The primary focus
          // lives at `/screen/program_key` in the standard flow but at
          // `/screen/substance` in the CAB seed; both are read (same value
          // vocabulary + labels) and duplicate phrases are collapsed, so either
          // flow resolves the same single primary term.
          targetPath: SUMMARY_FOCUS_PATH,
          template: 'You want to focus on {values}.',
          joinWith: ' and ',
          fallback: 'Your focus areas give us a place to start.',
          sources: [
            { sourcePath: '/screen/secondary_focus', labels: FOCUS_SECONDARY_LABELS },
            { sourcePath: '/screen/program_key', labels: FOCUS_PRIMARY_LABELS },
            { sourcePath: '/screen/substance', labels: FOCUS_PRIMARY_LABELS },
          ],
        },
        {
          // "Your motivation includes improving your relationship." — joins the
          // member's selected motivations.
          targetPath: SUMMARY_MOTIVATION_PATH,
          template: 'Your motivation includes {values}.',
          joinWith: ', ',
          fallback: 'Your reasons for change matter here.',
          sources: [{ sourcePath: '/screen/motivation', labels: MOTIVATION_PHRASES }],
        },
      ],
    },
  };
}

function buildInitialWeekSummary(variant: UsageVariant, values: readonly number[]) {
  // Reuse the runtime calculator so the hydrated fallback and the
  // calculate_week_summary tool produce the identical view-model shape and
  // accessibility labels — no second encoding to drift out of sync.
  return calculateWeekSummary(values, {
    activeColor: WEEK_SUMMARY_ACTIVE_COLOR,
    freeColor: WEEK_SUMMARY_FREE_COLOR,
    totalLabel: variant.totalLabel,
    freeLabel: variant.freeLabel,
    dayLabels: USAGE_DAY_PICKERS.map((day) => day.label),
    dayAccessibilitySuffix: variant.dayAccessibilitySuffix,
    dayAccessibilitySuffixOne: variant.dayAccessibilitySuffixOne,
  });
}

// Composes the ExpandableCallout element (plus its disclosed content) for a
// variant's unit guidance. Equivalents render as a horizontal row of
// icon+label columns separated by "=", text renders as a single paragraph.
function buildUnitGuideElements(
  guide: UsageUnitGuide,
  testIdPrefix: string,
): Record<string, CatalogElement> {
  const elements: Record<string, CatalogElement> = {};
  let calloutChildren: string[];

  if (guide.kind === 'text') {
    elements.unitCalloutBody = {
      type: 'Text',
      props: { variant: 'b2Regular', color: 'primary', value: guide.body },
    };
    calloutChildren = ['unitCalloutBody'];
  } else {
    const rowChildren: string[] = [];
    guide.items.forEach((item, index) => {
      const itemId = `unitItem${index}`;
      const iconId = `${itemId}Icon`;
      const labelId = `${itemId}Label`;
      const sublabelId = `${itemId}Sub`;
      elements[iconId] = {
        type: 'Icon',
        props: { name: item.icon, size: 24, tone: 'primary' },
      };
      elements[labelId] = {
        type: 'Text',
        props: { variant: 'c2Regular', color: 'primary', align: 'center', value: item.label },
      };
      const itemChildren = [iconId, labelId];
      if (item.sublabel) {
        elements[sublabelId] = {
          type: 'Text',
          props: { variant: 'c2Regular', color: 'primary', align: 'center', value: item.sublabel },
        };
        itemChildren.push(sublabelId);
      }
      elements[itemId] = {
        type: 'Stack',
        props: { direction: 'vertical', gap: 'xs', align: 'center' },
        children: itemChildren,
      };
      rowChildren.push(itemId);
      if (index < guide.items.length - 1) {
        const separatorId = `unitSep${index}`;
        elements[separatorId] = {
          type: 'Text',
          props: { variant: 'b2Bold', color: 'primary', value: '=' },
        };
        rowChildren.push(separatorId);
      }
    });
    elements.unitEquivalents = {
      type: 'Stack',
      props: { direction: 'horizontal', gap: 'xs', align: 'center', justify: 'space-between' },
      children: rowChildren,
    };
    calloutChildren = ['unitEquivalents'];
  }

  elements.unitCallout = {
    type: 'ExpandableCallout',
    props: { title: guide.title, testID: `${testIdPrefix}-unit-callout` },
    children: calloutChildren,
  };
  return elements;
}

function buildUsagePickerScreens(kind: 'weekly' | 'typical'): OnboardingScreens {
  const isTypical = kind === 'typical';
  const base: UsageStepBase = isTypical ? 'typical_drinks' : 'weekly_drinks';
  const screenId = isTypical ? TYPICAL_USAGE_SCREEN_ID : WEEKLY_USAGE_SCREEN_ID;
  const pickerPrefix = isTypical ? 'typical_' : '';
  const testIdPrefix = isTypical ? 'onboarding-typical-usage' : 'onboarding-weekly-usage';

  return Object.fromEntries(
    ALL_USAGE_VARIANTS.map((variant) => {
      const pickerIds = USAGE_DAY_PICKERS.map(({ key }) => `${pickerPrefix}${key}`);
      const elements: Record<string, CatalogElement> = {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId,
            accessibilityTitle: isTypical
              ? variant.typicalAccessibilityTitle
              : variant.weeklyAccessibilityTitle,
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: isTypical
            ? ['heading', 'subtitle', 'columnHeaders', 'days']
            : variant.unitGuide
              ? ['heading', 'subtitle', 'unitCallout', 'days']
              : ['heading', 'subtitle', 'days'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: isTypical
              ? 'What would a more typical week look like?'
              : 'Think back to last week.',
            role: 'header',
            testID: `${testIdPrefix}-heading`,
          },
        },
        subtitle: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: isTypical ? variant.typicalSubtitle : variant.weeklySubtitle,
            // The subtitle is the only visible per-programme copy on this
            // screen (the heading is shared), so e2e anchors on it.
            testID: `${testIdPrefix}-subtitle`,
          },
        },
        days: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: pickerIds,
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Continue',
            testID: `${testIdPrefix}-continue-button`,
          },
          on: {
            press: isTypical
              ? [
                  buildCalculateWeekSummaryTool('typical', variant, TYPICAL_WEEK_SUMMARY_PATH),
                  buildDeriveSummaryBulletsTool(),
                  { tool_name: 'advance_flow' },
                ]
              : [
                  buildCalculateWeekSummaryTool('weekly', variant, BASELINE_WEEK_SUMMARY_PATH),
                  { tool_name: 'advance_flow' },
                ],
          },
        },
      };

      if (isTypical) {
        // Column headers over the two value columns: the day-label column
        // (flex:1), the reference-tile column (REFERENCE_COLUMN_WIDTH, shared
        // with NumberPicker's own reference-tile column below — see
        // NumberPicker.ts), a gap matching the tile's trailing margin, and the
        // stepper column ("Typical week" itself widened to
        // INLINE_STEPPER_ROW_WIDTH). Both value columns share their exact
        // width with the day rows below, so the header and every row align by
        // construction — not by eyeballed offsets — regardless of the day
        // row's reference value width ("0" vs "30") or either label's own
        // rendered text width.
        elements.columnHeaders = {
          type: 'Stack',
          props: { direction: 'horizontal', align: 'center' },
          children: [
            'columnHeaderLabelSpacer',
            'columnHeaderReferenceColumn',
            'columnHeaderTileGap',
            'typicalWeekHeader',
          ],
        };
        elements.columnHeaderLabelSpacer = {
          type: 'Stack',
          props: { flex: 1 },
        };
        elements.columnHeaderReferenceColumn = {
          type: 'Stack',
          props: { direction: 'horizontal', justify: 'center', width: REFERENCE_COLUMN_WIDTH },
          children: ['lastWeekHeader'],
        };
        elements.columnHeaderTileGap = {
          type: 'Stack',
          props: { width: STEPPER_GAP },
        };
        elements.lastWeekHeader = {
          type: 'Label',
          props: {
            label: 'Last 7 days',
            backgroundColor: LAST_WEEK_HEADER_BACKGROUND,
            testID: `${testIdPrefix}-last-week-header`,
          },
        };
        elements.typicalWeekHeader = {
          type: 'Label',
          props: {
            label: 'Typical week',
            width: INLINE_STEPPER_ROW_WIDTH,
            backgroundColor: TYPICAL_WEEK_HEADER_BACKGROUND,
            textColor: TYPICAL_WEEK_HEADER_TEXT_COLOR,
            testID: `${testIdPrefix}-typical-week-header`,
          },
        };
      }

      // The "what counts as one <unit>?" expandable callout — weekly picker
      // only, and only for variants that carry unit guidance.
      if (!isTypical && variant.unitGuide) {
        Object.assign(elements, buildUnitGuideElements(variant.unitGuide, testIdPrefix));
      }

      for (const day of USAGE_DAY_PICKERS) {
        const elementId = `${pickerPrefix}${day.key}`;
        const field = isTypical ? day.typicalField : day.field;
        elements[elementId] = {
          type: 'NumberPicker',
          props: {
            label: day.label,
            decrementAccessibilityLabel: `Decrease count for ${day.label}`,
            incrementAccessibilityLabel: `Increase count for ${day.label}`,
            value: { $bindState: `/screen/${field}` },
            // On the typical (comparison) screen each row shows last week's
            // value as a read-only "Last 7 days" reference tile beside the
            // editable typical value.
            ...(isTypical
              ? {
                  referenceValue: { $bindState: `/screen/${day.field}` },
                  referenceAccessibilityLabel: 'Last week',
                }
              : {}),
            min: 0,
            max: 30,
            step: 1,
            layout: 'inline',
            testID: `${testIdPrefix}-number-picker-${day.key}`,
          },
        };
      }

      const initialState = Object.fromEntries(
        USAGE_DAY_PICKERS.map((day) => [isTypical ? day.typicalField : day.field, 0]),
      );

      return [
        usageStepKey(base, variant),
        {
          screen_id: screenId,
          spec: {
            root: 'screen',
            elements,
          },
          initial_state: { screen: initialState },
        },
      ];
    }),
  );
}

function buildWeekSummaryScreens(): OnboardingScreens {
  return Object.fromEntries(
    ALL_USAGE_VARIANTS.map((variant) => {
      const dayContainerIds = USAGE_DAY_PICKERS.map((day) => `day${capitalize(day.key)}`);
      const elements: Record<string, CatalogElement> = {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: WEEK_SUMMARY_SCREEN_ID,
            accessibilityTitle: 'Does this look like a typical week for you?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['heading', 'body', 'metrics', 'weekGrid', 'legend'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: 'Does this look like a typical week for you?',
            role: 'header',
          },
        },
        body: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: "No worries if not — we'll just get a clearer picture of your usual.",
          },
        },
        metrics: {
          type: 'Stack',
          props: { direction: 'horizontal', gap: 'sm', align: 'stretch' },
          children: ['metricTotalCell', 'metricFreeCell'],
        },
        metricTotalCell: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none', flex: 1 },
          children: ['metricTotal'],
        },
        metricFreeCell: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none', flex: 1 },
          children: ['metricFree'],
        },
        metricTotal: {
          type: 'MetricCard',
          props: {
            kind: 'badge',
            badge: { $bindState: `${BASELINE_WEEK_SUMMARY_PATH}/totalBadge` },
            badgeColor: WEEK_SUMMARY_ACTIVE_COLOR,
            label: variant.totalLabel,
            value: 'Per week',
            accessibilityLabel: {
              $bindState: `${BASELINE_WEEK_SUMMARY_PATH}/totalAccessibilityLabel`,
            },
          },
        },
        metricFree: {
          type: 'MetricCard',
          props: {
            kind: 'badge',
            badge: { $bindState: `${BASELINE_WEEK_SUMMARY_PATH}/freeBadge` },
            badgeColor: WEEK_SUMMARY_FREE_COLOR,
            label: variant.freeLabel,
            value: 'Days per week',
            accessibilityLabel: {
              $bindState: `${BASELINE_WEEK_SUMMARY_PATH}/freeAccessibilityLabel`,
            },
          },
        },
        weekGrid: {
          type: 'Stack',
          props: { direction: 'horizontal', gap: 'xs', justify: 'space-between' },
          children: dayContainerIds,
        },
        legend: {
          type: 'Stack',
          props: { direction: 'horizontal', gap: 'm' },
          children: ['legendFree', 'legendDrinking'],
        },
        legendFree: {
          type: 'LegendItem',
          props: { color: WEEK_SUMMARY_FREE_COLOR, label: variant.freeLegendLabel },
        },
        legendDrinking: {
          type: 'LegendItem',
          props: { color: WEEK_SUMMARY_ACTIVE_COLOR, label: variant.activeLegendLabel },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'sm' },
          children: ['typicalButton', 'atypicalButton'],
        },
        typicalButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: "Yes, that's pretty typical",
            testID: 'onboarding-week-summary-typical-button',
          },
          on: {
            press: [
              {
                tool_name: 'set_state',
                parameters: { path: '/screen/week_typical', value: 'yes' },
              },
              buildCalculateWeekSummaryTool('weekly', variant, TYPICAL_WEEK_SUMMARY_PATH),
              buildDeriveSummaryBulletsTool(),
              { tool_name: 'advance_flow' },
            ],
          },
        },
        atypicalButton: {
          type: 'TextButton',
          props: {
            state: 'Default',
            label: "No, this isn't a typical week",
            testID: 'onboarding-week-summary-atypical-button',
          },
          on: {
            press: [
              {
                tool_name: 'set_state',
                parameters: { path: '/screen/week_typical', value: 'no' },
              },
              { tool_name: 'advance_flow' },
            ],
          },
        },
      };

      USAGE_DAY_PICKERS.forEach((day, index) => {
        const containerId = `day${capitalize(day.key)}`;
        const labelId = `${containerId}Label`;
        const tileId = `${containerId}Tile`;
        elements[containerId] = {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'xs', align: 'center' },
          children: [labelId, tileId],
        };
        elements[labelId] = {
          type: 'Text',
          props: { variant: 'b2Regular', color: 'secondary', value: day.shortLabel },
        };
        elements[tileId] = {
          type: 'NumberTile',
          props: {
            value: { $bindState: `${BASELINE_WEEK_SUMMARY_PATH}/days/${index}/value` },
            backgroundColor: {
              $bindState: `${BASELINE_WEEK_SUMMARY_PATH}/days/${index}/backgroundColor`,
            },
            accessibilityLabel: {
              $bindState: `${BASELINE_WEEK_SUMMARY_PATH}/days/${index}/accessibilityLabel`,
            },
          },
        };
      });

      return [
        usageStepKey('week_summary', variant),
        {
          screen_id: WEEK_SUMMARY_SCREEN_ID,
          spec: {
            root: 'screen',
            elements,
          },
          initial_state: {
            screen: {
              baseline_week_summary: buildInitialWeekSummary(variant, WEEK_SUMMARY_VALUES),
            },
          },
        },
      ];
    }),
  );
}

// The usage branch hangs off sona_intro: the Meet Sona screen's final CTA
// resolves these rules through the host's navigation evaluation, so the branch
// needs no interstitial screen of its own to carry it.
function buildUsageNavigation(): NavigationRule[] {
  return [
    // In each programme's priority slot, its refinements come first, so a
    // member who answered the nicotine type takes that type's screens and
    // everyone else falls through to the programme's own.
    ...USAGE_VARIANTS_BY_PRIMARY_PRIORITY.flatMap((variant) => [
      ...USAGE_SUBTYPE_VARIANTS.filter((sub) => sub.programKey === variant.programKey).flatMap(
        (sub) =>
          usageBranchConditions(variant).map((when) => ({
            from_step_key: 'sona_intro',
            target_step_key: usageStepKey('weekly_drinks', sub),
            when: { ...when, ...sub.branchState },
          })),
      ),
      ...usageBranchConditions(variant).map((when) => ({
        from_step_key: 'sona_intro',
        target_step_key: usageStepKey('weekly_drinks', variant),
        when,
      })),
    ]),
    // Catch-all for any selection with no matching variant. It targets the
    // neutral usage screens (substance-free copy), never the alcohol screens.
    // A completeness test asserts every select option has its own variant, so
    // this is a safety net rather than a live path.
    {
      from_step_key: 'sona_intro',
      target_step_key: usageStepKey('weekly_drinks', NEUTRAL_USAGE_VARIANT),
    },
    ...ALL_USAGE_VARIANTS.flatMap((variant) => {
      const weeklyStep = usageStepKey('weekly_drinks', variant);
      const summaryGateStep = usageStepKey('week_summary', variant);
      const typicalStep = usageStepKey('typical_drinks', variant);
      const summaryStep = 'rewards_intro';
      // Branches with a TAPS 2 set exit the baseline (typical-yes shortcut and
      // typical picker alike) into their first screening step; everyone else
      // goes straight to rewards. Data-driven off TAPS2_STEP_CHAINS — reaching
      // a program's own week_summary/typical steps already means it is the
      // member's primary, so no runtime condition is needed here.
      const chain = variant.programKey !== null ? TAPS2_STEP_CHAINS[variant.programKey] : undefined;
      const baselineExitStep = chain?.[0] ?? summaryStep;

      return [
        { from_step_key: weeklyStep, target_step_key: summaryGateStep },
        {
          from_step_key: summaryGateStep,
          target_step_key: baselineExitStep,
          when: { '/screen/week_typical': 'yes' },
        },
        {
          from_step_key: summaryGateStep,
          target_step_key: typicalStep,
          when: { '/screen/week_typical': 'no' },
        },
        { from_step_key: typicalStep, target_step_key: baselineExitStep },
      ];
    }),
    // Each declaring program's screening steps run in chain order, then rejoin
    // the rewards introduction. Reaching a chain already implies the
    // program is the member's primary, so every edge is unconditional.
    ...TAPS2_PROGRAM_KEYS.flatMap((programKey): NavigationRule[] => {
      const variant = USAGE_VARIANTS.find((candidate) => candidate.programKey === programKey);
      if (!variant) return [];
      const chain = TAPS2_STEP_CHAINS[programKey];
      const summaryStep = 'rewards_intro';
      return chain.map((stepKey, index) => ({
        from_step_key: stepKey,
        target_step_key: index + 1 < chain.length ? chain[index + 1] : summaryStep,
      }));
    }),
  ];
}

// ---------------------------------------------------------------------------
// TAPS 2 yes/no screening screens. All share the onboarding-taps2 screen
// identity (like the goal and usage variants share theirs); testIDs derive
// from the step key so consecutive steps in a chain stay distinguishable to
// the e2e runner. Whether "yes" is a positive screen lives in the catalog's
// `positive_values`, never here.
// ---------------------------------------------------------------------------

function buildTaps2YesNoScreens(): OnboardingScreens {
  return Object.fromEntries(
    TAPS2_YES_NO_QUESTIONS.map((question): [string, FlowScreen] => {
      const idPrefix = `onboarding-${question.stepKey.replace(/_/g, '-')}`;
      return [
        question.stepKey,
        {
          screen_id: 'onboarding-taps2',
          spec: {
            root: 'screen',
            elements: {
              screen: {
                type: 'Screen',
                props: {
                  contentPaddingHorizontal: 'lg',
                  footerPaddingHorizontal: 'lg',
                  screenId: 'onboarding-taps2',
                  accessibilityTitle: question.heading,
                  scrollable: true,
                  hasFooter: true,
                  footerDivider: false,
                  edges: ['left', 'right', 'bottom'],
                },
                children: ['content', 'footer'],
              },
              content: {
                type: 'Stack',
                props: { direction: 'vertical', gap: 'lg' },
                children: ['headingGroup', 'select'],
              },
              headingGroup: {
                type: 'Stack',
                props: { direction: 'vertical', gap: 's' },
                children: ['heading', 'hint'],
              },
              heading: {
                type: 'Text',
                props: {
                  variant: 'h1Bold28',
                  color: 'primary',
                  value: question.heading,
                  role: 'header',
                },
              },
              hint: {
                type: 'Text',
                props: {
                  variant: 'b1Regular',
                  color: 'secondary',
                  value: question.hint,
                },
              },
              select: {
                type: 'SingleSelect',
                props: {
                  label: question.label,
                  hideLabel: true,
                  value: { $bindState: `/screen/${question.stepKey}` },
                  testID: `${idPrefix}-single-select`,
                  options: [
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ],
                },
              },
              footer: {
                type: 'Stack',
                props: { direction: 'vertical', gap: 'none' },
                children: ['continueButton'],
              },
              continueButton: {
                type: 'Button',
                props: {
                  size: 'L',
                  type: 'Primary',
                  state: 'Default',
                  icon: false,
                  text: true,
                  label: 'Continue',
                  testID: `${idPrefix}-continue-button`,
                },
                on: {
                  press: { tool_name: 'advance_flow' },
                },
              },
            },
          },
          validation: [
            {
              path: `/screen/${question.stepKey}`,
              type: 'required',
              message: 'Pick an option to continue.',
            },
          ],
        },
      ];
    }),
  );
}

// ---------------------------------------------------------------------------
// Goal-step variants. The goal descriptions reference the member's substance,
// so — like the usage steps — the step is authored once per program plus a
// neutral fallback, all sharing the `onboarding-goal` screen identity, testIDs,
// and option values. Which variant renders is decided by navigation data keyed
// off the member's own selection, never by code branching on a program key.
// ---------------------------------------------------------------------------

type GoalVariant = {
  /** null = the neutral fallback step (poly selections, unknown values). */
  programKey: string | null;
  stepKey: string;
  stopDescription: string;
  cutBackDescription: string;
  maintainLabel: string;
};

export const GOAL_VARIANTS: GoalVariant[] = [
  {
    programKey: 'alcohol',
    stepKey: 'goal_alcohol',
    stopDescription: 'No more drinking - a full reset.',
    cutBackDescription: 'Drink less, feel more intentional about when and why.',
    maintainLabel: "I'm already sober and want to maintain",
  },
  {
    programKey: 'tobacco',
    stepKey: 'goal_tobacco',
    stopDescription: 'No more - a full reset.',
    cutBackDescription: 'Use less, feel more intentional about when and why.',
    maintainLabel: "I've already quit and want to maintain",
  },
  {
    programKey: 'opioid',
    stepKey: 'goal_opioid',
    stopDescription: 'No more - a full reset.',
    cutBackDescription: 'Use less, feel more intentional about when and why.',
    maintainLabel: "I've already stopped and want to maintain",
  },
  {
    programKey: 'stimulants',
    stepKey: 'goal_stimulants',
    stopDescription: 'No more use - a full reset.',
    cutBackDescription: 'Use less, feel more intentional about when and why.',
    maintainLabel: "I've already stopped and want to maintain",
  },
  {
    programKey: 'cannabis',
    stepKey: 'goal_cannabis',
    stopDescription: 'No more cannabis - a full reset.',
    cutBackDescription: 'Use less, feel more intentional about when and why.',
    maintainLabel: "I've already stopped and want to maintain",
  },
  {
    programKey: null,
    stepKey: 'goal',
    stopDescription: 'No more - a full reset.',
    cutBackDescription: 'Use less, feel more intentional about when and why.',
    maintainLabel: "I've already stopped and want to maintain",
  },
];

/** The six goal options; values are shared across variants so downstream answer handling never varies. */
function buildGoalOptions(variant: GoalVariant) {
  return [
    {
      label: 'I want to stop completely',
      value: 'stop',
      description: variant.stopDescription,
    },
    {
      label: 'I want to cut back',
      value: 'cut_back',
      description: variant.cutBackDescription,
    },
    {
      label: 'I want to understand my patterns better',
      value: 'understand',
      description: "See what's really going on before making changes.",
    },
    {
      label: "I'm figuring out if I need to make any changes",
      value: 'exploring',
      description: 'Exploring is a great place to start.',
    },
    {
      label: variant.maintainLabel,
      value: 'sober',
      description: "Stay steady. Keep what you've built.",
    },
    {
      label: "I'm not sure yet",
      value: 'unsure',
      description: "That's completely okay. We'll figure it out together.",
    },
  ];
}

function buildGoalScreens(): OnboardingScreens {
  return Object.fromEntries(
    GOAL_VARIANTS.map((variant) => [
      variant.stepKey,
      {
        screen_id: 'onboarding-goal',
        spec: {
          root: 'screen',
          elements: {
            screen: {
              type: 'Screen',
              props: {
                contentPaddingHorizontal: 'lg',
                footerPaddingHorizontal: 'lg',
                screenId: 'onboarding-goal',
                accessibilityTitle: 'Choose your goal',
                scrollable: true,
                hasFooter: true,
                footerDivider: false,
                edges: ['left', 'right', 'bottom'],
              },
              children: ['content', 'footer'],
            },
            content: {
              type: 'Stack',
              props: { direction: 'vertical', gap: 'lg' },
              children: ['headingGroup', 'select'],
            },
            headingGroup: {
              type: 'Stack',
              props: { direction: 'vertical', gap: 's' },
              children: ['heading', 'hint'],
            },
            heading: {
              type: 'Text',
              props: {
                variant: 'h1Bold28',
                color: 'primary',
                value: 'What feels right for you right now?',
                role: 'header',
              },
            },
            hint: {
              type: 'Text',
              props: {
                variant: 'b1Regular',
                color: 'secondary',
                value: "This can change anytime. We'll check in.",
              },
            },
            select: {
              type: 'SingleSelect',
              props: {
                label: 'Your goal',
                hideLabel: true,
                value: { $bindState: '/screen/goal' },
                testID: 'onboarding-goal-single-select',
                options: buildGoalOptions(variant),
              },
            },
            footer: {
              type: 'Stack',
              props: { direction: 'vertical', gap: 'none' },
              children: ['continueButton'],
            },
            continueButton: {
              type: 'Button',
              props: {
                size: 'L',
                type: 'Primary',
                state: 'Default',
                icon: false,
                text: true,
                label: 'Continue',
                testID: 'onboarding-goal-continue-button',
              },
              on: {
                press: { tool_name: 'advance_flow' },
              },
            },
          },
        },
        validation: [
          {
            path: '/screen/goal',
            type: 'required' as const,
            message: 'Pick a goal to continue.',
          },
        ],
      },
    ]),
  );
}

function buildGoalNavigation(): NavigationRule[] {
  return [
    // A single-program selection routes to its variant; legacy scalar answers
    // (stored under /screen/substance) route via the usage variants' legacy
    // value lists. Anything else (poly, unknown, absent) falls through to the
    // neutral `goal` step.
    ...GOAL_VARIANTS.flatMap((variant): NavigationRule[] => {
      if (variant.programKey === null) return [];
      const programKey = variant.programKey;
      const usage = USAGE_VARIANTS.find((u) => u.programKey === variant.programKey);
      const legacyValues = usage?.legacyProgramValues ?? [];
      return [
        {
          from_step_key: 'motivation',
          target_step_key: variant.stepKey,
          when: { '/screen/program_key': { $in: [programKey], $count: 1 } },
        },
        {
          from_step_key: 'motivation',
          target_step_key: variant.stepKey,
          when: {
            '/screen/substance': {
              $in: [programKey, ...legacyValues],
              $count: 1,
            },
          },
        },
      ];
    }),
    { from_step_key: 'motivation', target_step_key: 'goal' },
    ...GOAL_VARIANTS.map((variant) => ({
      from_step_key: variant.stepKey,
      target_step_key: 'change_history',
    })),
  ];
}

function capitalize(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

// ---------------------------------------------------------------------------
// Screen specs
// ---------------------------------------------------------------------------

const screens: OnboardingScreens = {
  // ------------------------------------------------------------------
  // Start (welcoming character-illustration entry)
  // ------------------------------------------------------------------
  start: {
    screen_id: 'onboarding-start',
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-start',
            accessibilityTitle: "Let's see where you're at",
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg', align: 'center', justify: 'center' },
          children: ['illustration', 'headingGroup'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'sm', align: 'center' },
          children: ['heading', 'body'],
        },
        illustration: {
          type: 'CharacterIllustration',
          props: {
            name: 'looking-ahead-sm',
            size: 'L',
            accessibilityLabel: 'Person looking ahead toward the horizon',
          },
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            align: 'center',
            value: "Let's see where\nyou're at",
            role: 'header',
          },
        },
        body: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            align: 'center',
            value:
              "A few quick questions to understand where you're starting from. Your answers shape what Pelago focuses on with you.",
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Begin',
            testID: 'onboarding-start-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
  },

  // ------------------------------------------------------------------
  // Name
  // ------------------------------------------------------------------
  name: {
    screen_id: 'onboarding-name',
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-name',
            accessibilityTitle: 'What should we call you?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['headingGroup', 'input'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['heading', 'body'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: 'First, what should we call you?',
            role: 'header',
          },
        },
        body: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: 'This helps us make the experience feel more personal from the start.',
          },
        },
        input: {
          type: 'TextInput',
          props: {
            label: 'Preferred name',
            value: { $bindState: '/screen/preferred_name' },
            placeholder: 'Enter your preferred name',
            helperText: 'Use the name you want your guide to use in conversation.',
            required: true,
            autoFocus: true,
            autofillPurpose: 'preferred-name',
            testID: 'onboarding-name-text-input',
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Continue',
            testID: 'onboarding-name-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
    validation: [
      { path: '/screen/preferred_name', type: 'required', message: 'Enter your preferred name.' },
    ],
  },

  // ------------------------------------------------------------------
  // Motivation
  // ------------------------------------------------------------------
  motivation: {
    screen_id: 'onboarding-motivation',
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-motivation',
            accessibilityTitle: 'What is motivating you?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            // Footer CTA stays pinned behind the keyboard while typing the
            // free-text answer; the member dismisses the keyboard to reveal it.
            footerLiftsWithKeyboard: false,
            // Keep the focused free-text input visible above the keyboard.
            keyboardAwareContent: true,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['headingGroup', 'select', 'otherInput'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['heading', 'hint'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: "What's motivating you right now?",
            role: 'header',
          },
        },
        hint: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: "There's no wrong answer. This helps us understand where you're coming from.",
          },
        },
        select: {
          type: 'MultiSelect',
          props: {
            label: 'Your motivation',
            hideLabel: true,
            value: { $bindState: '/screen/motivation' },
            testID: 'onboarding-motivation-multi-select',
            options: [
              { label: 'My health - physical or mental', value: 'health' },
              { label: 'A relationship or someone I care about', value: 'relationship' },
              { label: 'I want to feel more in control', value: 'control' },
              { label: 'A doctor or provider recommended it', value: 'provider' },
              { label: "I'm just curious about my habits", value: 'curious' },
            ],
          },
        },
        otherInput: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'sm' },
          children: ['otherLabel', 'otherField'],
        },
        otherLabel: {
          type: 'Text',
          props: { variant: 'b2Bold', color: 'primary', value: 'Something else' },
        },
        otherField: {
          type: 'TextInput',
          props: {
            label: 'Something else',
            hideLabel: true,
            value: { $bindState: '/screen/motivation_other' },
            placeholder: 'Add your own',
            testID: 'onboarding-motivation-other-text-input',
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Continue',
            testID: 'onboarding-motivation-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
    initial_state: { screen: { motivation: [] } },
    validation: [
      {
        path: '/screen/motivation',
        type: 'required',
        message: 'Pick at least one motivation to continue.',
      },
    ],
  },

  // ------------------------------------------------------------------
  // 3. Primary program
  // ------------------------------------------------------------------
  substance: {
    screen_id: 'onboarding-substance',
    spec: {
      root: 'screen',
      elements: {
        // `watch` lives here (not on the sub-type section) because it must
        // fire even before "Tobacco or nicotine" is ever selected.
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-substance',
            accessibilityTitle: 'What are you looking to work on?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          watch: {
            // `currentValue` is a `{ $state }` binding, not a path string —
            // the renderer resolves it against its own live store at
            // dispatch time. Re-deriving the value from the path inside the
            // host tool handler would read the separate, slower-to-sync
            // FlowStateAdapter and can observe a stale snapshot from before
            // this very change. This clears a genuine side effect (a stale
            // prior sub-type answer) that can't be expressed as a pure
            // derivation — unlike the reveal's own expanded state below,
            // which is derived, not synced.
            '/screen/program_key': {
              tool_name: 'clear_state_unless_included',
              parameters: {
                currentValue: { $state: '/screen/program_key' },
                value: 'tobacco',
                targetPath: '/screen/tobacco_subtype',
              },
            },
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['headingGroup', 'selectionsGroup'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['heading', 'hint'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: 'What are you looking to work on?',
            role: 'header',
          },
        },
        hint: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: 'We can always shift later.',
          },
        },
        // The focus-area list is split around a conditional reveal: selecting
        // "Tobacco or nicotine" shows a nested, indented sub-type choice right
        // below it (Figma node 5889-35398). Both halves bind the same
        // `/screen/program_key` array — MultiSelect only adds/removes its own
        // option's value on toggle, so splitting the list doesn't disturb the
        // other half's selections. `selectionsGroup`'s gap matches MultiSelect's
        // own inter-option gap (OPTION_GAP/spacing.s) so the split reads as one
        // continuous list.
        selectionsGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['selectTop', 'tobaccoRevealWrapper', 'selectBottom'],
        },
        selectTop: {
          type: 'MultiSelect',
          props: {
            label: 'Focus area',
            hideLabel: true,
            value: { $bindState: '/screen/program_key' },
            testID: 'onboarding-substance-multi-select-top',
            options: [
              { label: 'Alcohol', value: 'alcohol' },
              { label: 'Tobacco or nicotine', value: 'tobacco' },
            ],
          },
        },
        // Indented under "Tobacco or nicotine" with a connector line, matching
        // the Figma reveal. Wrapped in `AnimatedReveal` (below) so the reveal
        // fades/slides in as a unit in sync with the connector line, rather
        // than hard-mounting via `visible`. `expanded` is *derived* directly
        // from the selection on every render (the same live prop-resolution
        // pipeline `visible` uses, via `$cond`) rather than synced through a
        // separate flag — a synced flag is two sources of truth for one
        // fact, and the write-back to it lags one render behind the
        // selection itself (it round-trips through the host-level
        // FlowStateAdapter before reaching this $bindState-bound prop),
        // which is exactly what let expand/collapse drift out of sync with
        // selection. The screen-level `watch` above still clears any prior
        // sub-type answer via `clear_state_unless_included` the moment
        // tobacco drops out of the selection — that's a real side effect
        // (a persisted answer to erase), not something derivable, so it
        // stays as an explicit tool call.
        tobaccoRevealWrapper: {
          type: 'AnimatedReveal',
          props: {
            expanded: {
              $cond: { $state: '/screen/program_key', includes: 'tobacco' },
              $then: true,
              $else: false,
            },
            testID: 'onboarding-substance-tobacco-reveal',
          },
          children: ['tobaccoSubtypeSection'],
        },
        tobaccoSubtypeSection: {
          type: 'Stack',
          props: { direction: 'horizontal', align: 'stretch' },
          children: ['tobaccoConnectorGutter', 'tobaccoSubtypeColumn'],
        },
        // Flush to the leading edge (not centered in the gutter) so the line
        // aligns with the parent "Tobacco or nicotine" card's own left edge.
        tobaccoConnectorGutter: {
          type: 'Stack',
          props: { direction: 'horizontal', justify: 'start', width: 24 },
          children: ['tobaccoConnectorLine'],
        },
        tobaccoConnectorLine: {
          type: 'Divider',
          props: { orientation: 'vertical', color: colors.stroke.divider },
        },
        tobaccoSubtypeColumn: {
          type: 'Stack',
          props: { direction: 'vertical', flex: 1 },
          children: ['tobaccoSubtypeSelect'],
        },
        tobaccoSubtypeSelect: {
          type: 'SingleSelect',
          props: {
            label: 'Tobacco or nicotine type',
            hideLabel: true,
            value: { $bindState: '/screen/tobacco_subtype' },
            testID: 'onboarding-substance-tobacco-subtype-select',
            options: [
              { label: 'Cigarettes', value: 'cigarettes' },
              { label: 'Vaping', value: 'vaping' },
              { label: 'Smokeless tobacco', value: 'smokeless' },
            ],
          },
        },
        selectBottom: {
          type: 'MultiSelect',
          props: {
            label: 'Focus area',
            hideLabel: true,
            value: { $bindState: '/screen/program_key' },
            testID: 'onboarding-substance-multi-select-bottom',
            options: [
              { label: 'Opioids', value: 'opioid' },
              { label: 'Stimulant', value: 'stimulants' },
              { label: 'Cannabis', value: 'cannabis' },
            ],
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        // Disabled until the answer is complete: at least one focus area,
        // and a sub-type whenever tobacco is among them. Derived on every
        // render from live state, like the reveal's `expanded` above. The
        // empty-selection test reads the array's first element
        // (`program_key/0`) because a bare `$state` on an empty array is
        // truthy. The screen's `validation` rules stay as the press-time
        // backstop for hosts that bind this button's state themselves.
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: {
              $cond: {
                $or: [
                  { $state: '/screen/program_key/0', not: true },
                  {
                    $and: [
                      { $state: '/screen/program_key', includes: 'tobacco' },
                      { $state: '/screen/tobacco_subtype', not: true },
                    ],
                  },
                ],
              },
              $then: 'Disabled',
              $else: 'Default',
            },
            icon: false,
            text: true,
            label: 'Continue',
            testID: 'onboarding-substance-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
    // `tobacco_subtype` starts unset — the member must actively pick a
    // sub-type; the Figma reference's pre-checked "Cigarettes" is an
    // illustrative mock state, not a real default. The reveal's expanded
    // state has no initial value of its own to seed — it's derived from
    // `program_key` on every render.
    initial_state: { screen: { program_key: [] } },
    validation: [
      {
        path: '/screen/program_key',
        type: 'required',
        message: 'Pick at least one focus area to continue.',
      },
      // Gated on the same array-membership condition as the reveal itself
      // (`when` mirrors a navigation rule's own condition shape): the member
      // must actually choose a sub-type once "Tobacco or nicotine" is
      // selected, not just have the option checked.
      {
        path: '/screen/tobacco_subtype',
        type: 'required',
        message: 'Pick a tobacco or nicotine type to continue.',
        when: { '/screen/program_key': 'tobacco' },
      },
    ],
  },

  // ------------------------------------------------------------------
  // 4. Condition (optional secondary focus)
  // ------------------------------------------------------------------
  condition: {
    screen_id: 'onboarding-condition',
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-condition',
            accessibilityTitle: 'Would you like to explore any of these other topics?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            // The footer CTA stays pinned behind the keyboard while typing the
            // free-text answer; the member dismisses the keyboard to reveal it.
            footerLiftsWithKeyboard: false,
            // Keep the focused free-text input visible above the keyboard.
            keyboardAwareContent: true,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['headingGroup', 'select', 'otherInput'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['heading', 'hint'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: 'Would you like to explore any of these other topics?',
            role: 'header',
          },
        },
        hint: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: 'Optional — tap a topic again to unselect it.',
          },
        },
        select: {
          type: 'SingleSelect',
          props: {
            label: 'Anything else',
            hideLabel: true,
            value: { $bindState: '/screen/secondary_focus' },
            // Zero-or-one: re-tapping the chosen topic clears it, so Continue is
            // the only way forward and an accidental tap can always be undone.
            allowDeselect: true,
            testID: 'onboarding-condition-single-select',
            options: [
              { label: 'Gambling', value: 'gambling' },
              { label: 'Gaming', value: 'gaming' },
              {
                label: 'Eating/Overeating',
                value: 'eating',
                description: 'Feeling out of control around food',
              },
            ],
          },
        },
        otherInput: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'sm' },
          children: ['otherLabel', 'otherField'],
        },
        otherLabel: {
          type: 'Text',
          props: { variant: 'b2Bold', color: 'primary', value: 'Something else' },
        },
        otherField: {
          type: 'TextInput',
          props: {
            label: 'Something else',
            hideLabel: true,
            value: { $bindState: '/screen/secondary_focus_other' },
            placeholder: 'Add your own',
            testID: 'onboarding-condition-other-text-input',
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'sm' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Continue',
            testID: 'onboarding-condition-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
  },

  // ------------------------------------------------------------------
  // 5. Goal (per-program variants + neutral fallback — see GOAL_VARIANTS)
  // ------------------------------------------------------------------
  ...buildGoalScreens(),

  // ------------------------------------------------------------------
  // Change history
  // ------------------------------------------------------------------
  change_history: {
    screen_id: 'onboarding-change-history',
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-change-history',
            accessibilityTitle: 'Have you tried making a change before?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['headingGroup', 'select'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['heading', 'hint'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: 'Have you tried making a change before?',
            role: 'header',
          },
        },
        hint: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: 'This helps us understand where you are in your journey.',
          },
        },
        select: {
          type: 'SingleSelect',
          props: {
            label: 'Change history',
            hideLabel: true,
            value: { $bindState: '/screen/change_history' },
            testID: 'onboarding-change-history-single-select',
            options: [
              { label: "Yes, I've tried before", value: 'tried_before' },
              { label: 'This is my first time thinking about it', value: 'first_time' },
              {
                label: "I've thought about it but never really tried",
                value: 'thought_about_it',
              },
            ],
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Continue',
            testID: 'onboarding-change-history-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
  },

  // ------------------------------------------------------------------
  // Cutback degree (follow-up shown only after a reported prior attempt —
  // a degree-of-cutback question presupposes an attempt to measure)
  // ------------------------------------------------------------------
  cutback_degree: {
    screen_id: 'onboarding-cutback-degree',
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-cutback-degree',
            accessibilityTitle: 'How much were you able to cut back?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['headingGroup', 'select'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['heading', 'hint'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: 'How much were you able to cut back?',
            role: 'header',
          },
        },
        hint: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: 'Every attempt counts, however it went.',
          },
        },
        select: {
          type: 'SingleSelect',
          props: {
            label: 'Cutback degree',
            hideLabel: true,
            value: { $bindState: '/screen/cutback_degree' },
            testID: 'onboarding-cutback-degree-single-select',
            options: [
              { label: 'I stopped completely for a while', value: 'stopped_completely' },
              { label: 'I cut back a lot', value: 'cut_back_a_lot' },
              { label: 'I cut back a little', value: 'cut_back_a_little' },
              { label: "It didn't really stick", value: 'no_lasting_change' },
            ],
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Continue',
            testID: 'onboarding-cutback-degree-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
  },

  ...buildUsagePickerScreens('weekly'),
  ...buildUsagePickerScreens('typical'),
  ...buildWeekSummaryScreens(),

  // ------------------------------------------------------------------
  // TAPS 2 screening — asked after the baseline exits, only on branches whose
  // program declares a set (see TAPS2_STEP_CHAINS). Tobacco's time-to-first-use
  // step is authored inline; the yes/no items share a built skeleton.
  // ------------------------------------------------------------------
  taps2_smoke_timing: {
    screen_id: 'onboarding-taps2',
    spec: {
      root: 'screen',
      elements: {
        screen: {
          type: 'Screen',
          props: {
            contentPaddingHorizontal: 'lg',
            footerPaddingHorizontal: 'lg',
            screenId: 'onboarding-taps2',
            accessibilityTitle: 'How soon after waking do you usually use tobacco or nicotine?',
            scrollable: true,
            hasFooter: true,
            footerDivider: false,
            edges: ['left', 'right', 'bottom'],
          },
          children: ['content', 'footer'],
        },
        content: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'lg' },
          children: ['headingGroup', 'select'],
        },
        headingGroup: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 's' },
          children: ['heading', 'hint'],
        },
        heading: {
          type: 'Text',
          props: {
            variant: 'h1Bold28',
            color: 'primary',
            value: 'How soon after waking do you usually use tobacco or nicotine?',
            role: 'header',
          },
        },
        hint: {
          type: 'Text',
          props: {
            variant: 'b1Regular',
            color: 'secondary',
            value: 'This helps us get a clearer picture of your pattern.',
          },
        },
        select: {
          type: 'SingleSelect',
          props: {
            label: 'Time to first use',
            hideLabel: true,
            value: { $bindState: '/screen/taps2_smoke_timing' },
            testID: 'onboarding-taps2-single-select',
            options: [
              { label: 'Within 5 minutes', value: 'within_5' },
              { label: '6-30 minutes', value: 'within_30' },
              { label: '31-60 minutes', value: 'within_60' },
              { label: 'More than 60 minutes', value: 'over_60' },
            ],
          },
        },
        footer: {
          type: 'Stack',
          props: { direction: 'vertical', gap: 'none' },
          children: ['continueButton'],
        },
        continueButton: {
          type: 'Button',
          props: {
            size: 'L',
            type: 'Primary',
            state: 'Default',
            icon: false,
            text: true,
            label: 'Continue',
            testID: 'onboarding-taps2-continue-button',
          },
          on: {
            press: { tool_name: 'advance_flow' },
          },
        },
      },
    },
    validation: [
      {
        path: '/screen/taps2_smoke_timing',
        type: 'required',
        message: 'Pick an option to continue.',
      },
    ],
  },

  ...buildTaps2YesNoScreens(),
};

// Reused-screen surface for sibling bundled flows (e.g. the CAB variant) that
// compose a subset of these authored screens under their own step keys. Exposed
// read-only; the onboarding flow's own output is unchanged.
export const onboardingScreens: Readonly<OnboardingScreens> = screens;

// ---------------------------------------------------------------------------
// Navigation rules
// ---------------------------------------------------------------------------

// The full onboarding navigation, including the edges through the two
// mobile-only steps (`sona_intro`, `rewards_intro`) whose screens the mobile
// composition supplies. The web composition derives its navigation from this
// array (rerouting around those steps) so the two platforms cannot silently
// diverge.
export const onboardingNavigation: NavigationRule[] = [
  // start -> name (advance off the start screen into the first question)
  { from_step_key: 'start', target_step_key: 'name' },
  // Linear path: name -> primary program
  { from_step_key: 'name', target_step_key: 'substance' },
  // primary program -> condition
  { from_step_key: 'substance', target_step_key: 'condition' },
  // condition -> motivation (asked after both selection screens so the
  // member declares a focus before saying why)
  { from_step_key: 'condition', target_step_key: 'motivation' },
  // motivation -> goal variant (single program) or neutral goal (poly /
  // unknown), then every variant -> change_history
  ...buildGoalNavigation(),
  // change_history -> cutback_degree only when a prior attempt was reported;
  // the conditional edge precedes the unconditional fallback (first match
  // wins), so first_time / thought_about_it / unanswered fall through to
  // sona_intro (the mobile-only Meet Sona screen), whose outbound edges are the usage
  // branch (see buildUsageNavigation)
  {
    from_step_key: 'change_history',
    target_step_key: 'cutback_degree',
    when: { '/screen/change_history': 'tried_before' },
  },
  { from_step_key: 'change_history', target_step_key: 'sona_intro' },
  { from_step_key: 'cutback_degree', target_step_key: 'sona_intro' },
  ...buildUsageNavigation(),
];

// Progress indicator covers the question steps only; the framing screens
// (start, introductions, and conversation mode) hide the indicator.
export const onboardingProgressStepKeys: NonNullable<FlowDefinition['progress_step_keys']> = [
  'name',
  'substance',
  'condition',
  'motivation',
  'goal',
  'change_history',
  'cutback_degree',
  'weekly_drinks',
  'week_summary',
  'typical_drinks',
  'taps2_smoke_timing',
  'taps2_alcohol_concern',
  'taps2_opioid_misuse',
  'taps2_opioid_concern',
  'taps2_cannabis_concern',
];

// Sectional progress (per-section dot counter): the pre-Sona question run,
// then the post-Sona usage-baseline run. The sona_intro story screen sits
// between the two and keeps its own story stepper — it is not a progress step.
export const onboardingProgressSections: NonNullable<FlowDefinition['progress_sections']> = [
  ['name', 'substance', 'condition', 'motivation', 'goal', 'change_history', 'cutback_degree'],
  [
    'weekly_drinks',
    'week_summary',
    'typical_drinks',
    'taps2_smoke_timing',
    'taps2_alcohol_concern',
    'taps2_opioid_misuse',
    'taps2_opioid_concern',
    'taps2_cannabis_concern',
  ],
];

/** Server-shaped synthetic program fixtures consumed by condition-agnostic preview clients. */
export const BINARY_USAGE_PREVIEW_PROGRAM_KEYS: readonly string[] = ['opioid', 'stimulants'];
